import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';

// A window opened by "open in a new window" carries its target in the URL.
// Capture it FIRST: this window has empty sessionStorage, so oidc-client-ts
// re-authenticates, and `onSigninCallback` rewrites the URL to the bare
// pathname — the query string is gone before any component renders.
// See utils/facilityWindow.ts.
import { captureHandoffFromUrl } from './utils/facilityWindow';
import { dropConnectionForHandoff } from './stores/connectionStore';
// A link opened in this same tab may name another connection than the one this tab
// picked earlier: drop that pick so the picker vets the link's connection.
dropConnectionForHandoff(captureHandoffFromUrl().connectionId);

// A tab left open across a deploy asks for page files the deploy replaced:
// reload once onto the new build instead of showing an error. See utils/staleBuild.ts.
import { installStaleBuildReload } from './utils/staleBuild';
installStaleBuildReload();

// Service worker: new builds are picked up quietly at the next section change.
// See utils/appUpdate.ts.
import { startAppUpdates } from './utils/appUpdate';
startAppUpdates();

// The Inter @font-face rules. First, so every stylesheet below can name it.
import './theme/fonts.css';
import './theme/cssVariables.css';
// Must load after cssVariables.css - it overrides those tokens under .ui-2026.
import './theme/ui2026.css';
// Must load after ui2026.css - `:root.high-contrast` ties with `:root.ui-2026`
// on specificity, so source order is what lets it override either base theme.
import './theme/highContrast.css';
import { resolveInitialUi2026, applyUi2026Class } from './theme/ui2026Flag';
import './theme/globalStyles.css';
import './theme/responsive.css';
import './theme/tab-common.css';
import './theme/toolbar.css';
// Shared control looks used outside their component's own stylesheet (the
// grid's checkbox in table cells, the record count footer).
import './theme/controls.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

// Stamp the refresh class before React mounts. themeStore applies it too, but
// that only runs when the store is first imported - which is lazy, and on a
// slow boot leaves a window where the old theme paints first. Doing it here
// makes the class present from the first frame. Idempotent, and persist=false
// so booting with the build default never writes a sticky user choice.
applyUi2026Class(resolveInitialUi2026(), false);
// Remove the initial loading screen after React mounts
const loadingEl = document.getElementById('loading-screen');
if (loadingEl) {
  loadingEl.style.opacity = '0';
  loadingEl.style.transition = 'opacity 0.3s ease';
  setTimeout(() => loadingEl.remove(), 300);
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);

// Tells the boot watchdog in index.html that the app started, so it stands down.
// Set here, at the end of this module, so it is in place before the main
// script's load event, which is when the watchdog's timer starts trusting it.
(window as Window & { __dietechBooted?: boolean }).__dietechBooted = true;

// Browser telemetry (page views, errors, load timings) to Application Insights.
// Loaded after the first render so it never delays startup; off on localhost
// and wherever the connection string is not set. See utils/telemetry.ts.
import { startTelemetry } from './utils/telemetry';
void startTelemetry();
